import { Link, useParams } from "react-router-dom";
import { texte, VIEWS, VIEW_LABEL, type ViewMode } from "../../texte";
import { useWorkspaces } from "../../state/workspaces";
import { useSettings } from "../../state/settings";
import { useUi } from "../../state/ui";
import { RailFileTree } from "./RailFileTree";

/**
 * Seitenleiste: Logo, Startseite, Arbeitsbereichsliste (mit Datei-Tree unter dem aktiven
 * Eintrag), "Neuer Arbeitsbereich" (nur Admin), Sicht-Umschalter, Zugangs-Chip.
 *
 * Route ist `#/ws/:token/:workspace` statt nur `#/ws/:token` — der reale Server
 * (awschat/plugins/botagent.py) bindet ein Token an ein Benutzerkonto, nicht an einen
 * einzelnen Arbeitsbereich; ohne den Workspace-Namen im Link wüsste der Client nicht,
 * welchen der ggf. mehreren Arbeitsbereiche dieses Kontos er öffnen soll.
 */
export function Rail() {
  const params = useParams<{ token?: string; workspace?: string }>();
  const apiUrl = useSettings((s) => s.apiUrl);
  const order = useWorkspaces((s) => s.order);
  const byName = useWorkspaces((s) => s.byName);
  const setView = useWorkspaces((s) => s.setView);
  const resetForWorkspace = useUi((s) => s.resetForWorkspace);
  const sessionView = useUi((s) => s.sessionView);
  const setSessionView = useUi((s) => s.setSessionView);

  const activeName = params.workspace ?? null;
  const activeEntry = activeName ? byName[activeName] : undefined;

  /**
   * WICHTIG: "admin-fähig" nur, wenn schon ein Workspace mit role:"admin" bekannt ist —
   * NICHT bei leerem Verlauf. Ein frisch geöffneter, noch unbekannter Link wird von
   * App.tsx (WorkspaceRoute) standardmäßig als "student" registriert, sofern sein Token
   * nicht bereits zu einem admin-Eintrag gehört; ein leerer Browser also automatisch als
   * "admin" zu behandeln, widerspräche dieser (bewusst restriktiveren) Registrierung.
   */
  const hasAdminWorkspace = Object.values(byName).some((w) => w.role === "admin");
  const sessionIsAdmin = hasAdminWorkspace;

  const role = activeEntry?.role ?? (sessionIsAdmin ? "admin" : "student");
  const view = activeEntry?.view ?? sessionView;
  const allowedViews: ViewMode[] =
    role === "admin" ? [...VIEWS] : activeEntry?.expertEnabled ? ["simple", "expert"] : ["simple"];

  function handleSetView(v: ViewMode) {
    if (activeEntry) setView(activeEntry.workspace, v);
    else setSessionView(v);
  }

  return (
    <nav className="rail">
      <div className="wordmark">
        <span className="mark">
          <img src="/TutorBot.png" alt="TutorBot" />
        </span>
        <span className="name">{texte.app.name}</span>
      </div>
      <Link className="rail-item" to="/" onClick={() => resetForWorkspace()}>
        <span>⌂</span> {texte.rail.home}
      </Link>
      <div className="rail-sep label">{texte.rail.workspacesSection}</div>
      {order.length === 0 ? (
        <div className="hint" style={{ padding: "4px 10px" }}>
          {texte.rail.noWorkspaces}
        </div>
      ) : (
        order.map((name) => {
          const entry = byName[name];
          if (!entry) return null;
          const isActive = activeName === name;
          return (
            <div key={name}>
              <Link
                className={`rail-item${isActive ? " active" : ""}`}
                to={`/ws/${entry.token}/${encodeURIComponent(name)}`}
                onClick={() => resetForWorkspace()}
              >
                <span className={`dot ${entry.lastKnown?.state ?? ""}`} />
                <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{name}</span>
              </Link>
              {isActive && <RailFileTree ctx={{ apiUrl, token: entry.token, workspace: name }} />}
            </div>
          );
        })
      )}
      {sessionIsAdmin && (
        <Link className="rail-item" to="/neu">
          <span>＋</span> {texte.rail.newWorkspace}
        </Link>
      )}
      <div className="spacer" />
      <div className="rail-sep label">{texte.rail.viewSection}</div>
      <div className="mode-switch" role="group" aria-label={texte.rail.viewSection}>
        {allowedViews.map((v) => (
          <button key={v} className={view === v ? "on" : ""} onClick={() => handleSetView(v)}>
            {VIEW_LABEL[v]}
          </button>
        ))}
      </div>
      <div className="user-chip">
        <span className="avatar">🔑</span>
        <span className="who">
          {texte.rail.accessActive}
          {role === "admin" && (
            <>
              <br />
              <span className="badge st-planned" style={{ marginTop: 3 }}>
                <span className="dot" />
                {texte.rail.admin}
              </span>
            </>
          )}
        </span>
      </div>
    </nav>
  );
}
